import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  DocumentArrowUpIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PlayIcon,
  ChevronRightIcon,
  EyeIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import TagEditor from './TagEditor';
import DataSourceSelector from './DataSourceSelector';


// R2-AUTH: /api/v1/legal/discovery/* now requires a Bearer token — attach it
// the same way the authed legal API client (services/api.js) does.
const discoveryAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('authToken');
  return token ? { ...extra, Authorization: `Bearer ${token}` } : extra;
};

const WIZARD_STEPS = {
  UPLOAD: 'upload',
  REVIEW: 'review', 
  CONFIGURE: 'configure'
};

export default function DiscoveryWizard({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(WIZARD_STEPS.UPLOAD);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  // Step 1: Upload state
  const [caseNameHint, setCaseNameHint] = useState('');
  
  // Step 2: Analysis results
  const [analysisResult, setAnalysisResult] = useState(null);
  const [editingTags, setEditingTags] = useState(false);
  
  // Step 3: Configuration
  const [selectedDataSource, setSelectedDataSource] = useState(null);
  const [processingOptions, setProcessingOptions] = useState({
    ingestToIndex: true,
    batchSize: 100
  });

  // File upload handler
  const onDrop = useCallback(async (acceptedFiles) => {
    setProcessing(true);
    setError(null);
    
    try {
      const formData = new FormData();
      acceptedFiles.forEach(file => {
        formData.append('files', file);
      });
      
      if (caseNameHint) {
        formData.append('case_name_hint', caseNameHint);
      }
      
      const response = await fetch('/api/v1/legal/discovery/analyze', {
        method: 'POST',
        headers: discoveryAuthHeaders(),
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setAnalysisResult(data.config);
      setCurrentStep(WIZARD_STEPS.REVIEW);
      
    } catch (err) {
      setError(err.message || 'Failed to analyze documents');
    } finally {
      setProcessing(false);
    }
  }, [caseNameHint]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/plain': ['.txt'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    multiple: true
  });

  // Save configuration and move to next step
  const handleSaveConfiguration = async () => {
    setProcessing(true);
    
    try {
      const response = await fetch('/api/v1/legal/discovery/configure', {
        method: 'POST',
        headers: discoveryAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(analysisResult)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save configuration');
      }
      
      setCurrentStep(WIZARD_STEPS.CONFIGURE);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Start discovery processing
  const handleStartProcessing = async () => {
    setProcessing(true);
    
    try {
      const response = await fetch(`/api/v1/legal/discovery/${analysisResult.case_id}/process`, {
        method: 'POST',
        headers: discoveryAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          data_source: selectedDataSource,
          ingest_to_index: processingOptions.ingestToIndex,
          batch_size: processingOptions.batchSize
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to start processing');
      }
      
      const data = await response.json();
      
      // Call parent completion handler
      onComplete({
        caseId: analysisResult.case_id,
        jobId: data.job_id,
        caseName: analysisResult.case_name
      });
      
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Progress indicator
  const getStepStatus = (step) => {
    const steps = Object.values(WIZARD_STEPS);
    const currentIndex = steps.indexOf(currentStep);
    const stepIndex = steps.indexOf(step);
    
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'upcoming';
  };

  const StepIndicator = () => (
    <div className="flex items-center justify-between mb-8">
      <div className="flex items-center space-x-8">
        {/* Step 1 */}
        <div className="flex items-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
            getStepStatus(WIZARD_STEPS.UPLOAD) === 'completed' 
              ? 'bg-green-500 text-white' 
              : getStepStatus(WIZARD_STEPS.UPLOAD) === 'current'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-500'
          }`}>
            {getStepStatus(WIZARD_STEPS.UPLOAD) === 'completed' ? (
              <CheckCircleIcon className="w-6 h-6" />
            ) : '1'}
          </div>
          <span className="ml-3 text-sm font-medium text-gray-900">Upload Documents</span>
        </div>
        
        <ChevronRightIcon className="w-5 h-5 text-gray-400" />
        
        {/* Step 2 */}
        <div className="flex items-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
            getStepStatus(WIZARD_STEPS.REVIEW) === 'completed' 
              ? 'bg-green-500 text-white' 
              : getStepStatus(WIZARD_STEPS.REVIEW) === 'current'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-500'
          }`}>
            {getStepStatus(WIZARD_STEPS.REVIEW) === 'completed' ? (
              <CheckCircleIcon className="w-6 h-6" />
            ) : '2'}
          </div>
          <span className="ml-3 text-sm font-medium text-gray-900">Review Tags</span>
        </div>
        
        <ChevronRightIcon className="w-5 h-5 text-gray-400" />
        
        {/* Step 3 */}
        <div className="flex items-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
            getStepStatus(WIZARD_STEPS.CONFIGURE) === 'completed' 
              ? 'bg-green-500 text-white' 
              : getStepStatus(WIZARD_STEPS.CONFIGURE) === 'current'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-500'
          }`}>
            {getStepStatus(WIZARD_STEPS.CONFIGURE) === 'completed' ? (
              <CheckCircleIcon className="w-6 h-6" />
            ) : '3'}
          </div>
          <span className="ml-3 text-sm font-medium text-gray-900">Configure Source</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Legal Discovery Intelligence</h1>
        <p className="text-gray-600">
          Upload court documents to automatically generate discovery tags, semantic bridges, and processing configurations.
        </p>
      </div>

      <StepIndicator />

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Upload Documents */}
      {currentStep === WIZARD_STEPS.UPLOAD && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <DocumentArrowUpIcon className="w-5 h-5 mr-2" />
              Upload Court Documents
            </h2>
            
            {/* Optional case name hint */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Case Name (Optional)
              </label>
              <input
                type="text"
                placeholder="e.g., Doe v. Acme Corp"
                value={caseNameHint}
                onChange={(e) => setCaseNameHint(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Providing a case name helps improve analysis accuracy
              </p>
            </div>
            
            {/* File dropzone */}
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input {...getInputProps()} />
              <DocumentArrowUpIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              {processing ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                  <span className="text-blue-800">Analyzing court documents...</span>
                </div>
              ) : (
                <>
                  <p className="text-lg text-gray-600 mb-2">
                    {isDragActive
                      ? 'Drop the court documents here...'
                      : 'Drag & drop court documents here, or click to select'}
                  </p>
                  <p className="text-sm text-gray-500">
                    Supports PDF, DOC, DOCX, TXT files • Multiple files supported
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Examples: Complaints, Dockets, Discovery Requests, Motions
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Review AI Analysis */}
      {currentStep === WIZARD_STEPS.REVIEW && analysisResult && (
        <div className="space-y-6">
          {/* Case Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center">
                <CheckCircleIcon className="w-5 h-5 mr-2 text-green-500" />
                AI Analysis Complete
              </h2>
              <div className="text-sm text-gray-500">
                Case ID: {analysisResult.case_id?.slice(0, 8)}...
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Case Information</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-600">Case Name:</span>
                    <span className="ml-2 text-sm text-gray-900">{analysisResult.case_name}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Parties:</span>
                    <div className="ml-2 text-sm text-gray-900">
                      {Object.entries(analysisResult.parties || {}).map(([type, parties]) => (
                        <div key={type} className="mt-1">
                          <span className="font-medium capitalize">{type}:</span> {parties.join(', ')}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Claims & Issues</h3>
                <div className="space-y-1">
                  {(analysisResult.claims || []).map((claim, idx) => (
                    <div key={idx} className="text-sm text-gray-700">• {claim}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Generated Tags */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">Generated Discovery Tags</h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  {(analysisResult.discovery_tags || []).filter(tag => tag.enabled).length} tags enabled
                </span>
                <button
                  onClick={() => setEditingTags(!editingTags)}
                  className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <PencilSquareIcon className="w-4 h-4 mr-1" />
                  {editingTags ? 'Done Editing' : 'Edit Tags'}
                </button>
              </div>
            </div>
            
            {editingTags ? (
              <TagEditor 
                tags={analysisResult.discovery_tags || []}
                onTagsChange={(updatedTags) => {
                  setAnalysisResult({
                    ...analysisResult,
                    discovery_tags: updatedTags
                  });
                }}
              />
            ) : (
              <div className="space-y-3">
                {(analysisResult.discovery_tags || []).map((tag) => (
                  <div 
                    key={tag.id} 
                    className={`border rounded-lg p-4 ${
                      tag.enabled ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="font-medium text-gray-900">{tag.name}</span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            tag.priority === 'high' ? 'bg-red-100 text-red-800' :
                            tag.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {tag.priority} priority
                          </span>
                          <span className="text-xs text-gray-500">
                            {Math.round(tag.confidence_threshold * 100)}% threshold
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">{tag.explanation}</p>
                        <div className="flex flex-wrap gap-1">
                          {(tag.search_terms || []).slice(0, 5).map((term, idx) => (
                            <span key={idx} className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded">
                              {term}
                            </span>
                          ))}
                          {tag.search_terms?.length > 5 && (
                            <span className="text-xs text-gray-500 px-2 py-1">
                              +{tag.search_terms.length - 5} more
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={`ml-4 w-4 h-4 rounded-full ${
                        tag.enabled ? 'bg-green-500' : 'bg-gray-300'
                      }`}></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex justify-between">
            <button
              onClick={() => setCurrentStep(WIZARD_STEPS.UPLOAD)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              ← Back
            </button>
            <button
              onClick={handleSaveConfiguration}
              disabled={processing}
              className="px-6 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
            >
              {processing ? 'Saving...' : 'Configure Data Source →'}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Configure Data Source */}
      {currentStep === WIZARD_STEPS.CONFIGURE && analysisResult && (
        <div className="space-y-6">
          {/* Configuration Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <CogIcon className="w-5 h-5 mr-2" />
              Ready to Process: {analysisResult.case_name}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-800">
                  {(analysisResult.discovery_tags || []).filter(tag => tag.enabled).length}
                </div>
                <div className="text-sm text-blue-600">Active Tags</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-800">3</div>
                <div className="text-sm text-green-600">Semantic Bridges</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-800">Ready</div>
                <div className="text-sm text-purple-600">Configuration</div>
              </div>
            </div>
          </div>

          {/* Data Source Selection */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="font-medium text-gray-900 mb-4">Select Data Source</h3>
            
            <DataSourceSelector 
              onDataSourceSelect={setSelectedDataSource}
              selectedDataSource={selectedDataSource}
            />
          </div>

          {/* Processing Options */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="font-medium text-gray-900 mb-4">Processing Options</h3>
            
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  id="ingest-to-index"
                  type="checkbox"
                  checked={processingOptions.ingestToIndex}
                  onChange={(e) => setProcessingOptions({
                    ...processingOptions,
                    ingestToIndex: e.target.checked
                  })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="ingest-to-index" className="ml-3 text-sm text-gray-700">
                  <span className="font-medium">Ingest to searchable index</span>
                  <div className="text-xs text-gray-500">Documents will be available for search and analysis in the platform</div>
                </label>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Batch Size
                  </label>
                  <select
                    value={processingOptions.batchSize}
                    onChange={(e) => setProcessingOptions({
                      ...processingOptions,
                      batchSize: parseInt(e.target.value)
                    })}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={50}>50 documents</option>
                    <option value={100}>100 documents</option>
                    <option value={200}>200 documents</option>
                    <option value={500}>500 documents</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-between">
            <button
              onClick={() => setCurrentStep(WIZARD_STEPS.REVIEW)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              ← Back
            </button>
            <button
              onClick={handleStartProcessing}
              disabled={!selectedDataSource || processing}
              className="flex items-center px-6 py-2 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 transition-colors"
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Starting...
                </>
              ) : (
                <>
                  <PlayIcon className="w-4 h-4 mr-2" />
                  Start Discovery Processing
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}